import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  UnViewedBadLogin,
  useGetUnViewedBadLoginsMutation,
} from '../../login/services/authenticationApi';

const UNVIEWED_BAD_LOGINS_SHOWN_KEY = 'unViewedBadLoginsShown';

let inFlightRequest: Promise<UnViewedBadLogin[] | null> | null = null;

export const resetUnViewedBadLoginsSession = () => {
  sessionStorage.removeItem(UNVIEWED_BAD_LOGINS_SHOWN_KEY);
  inFlightRequest = null;
};

const formatDateMinSecond = (date: string) => {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm:ss') : date;
};

const UnViewedBadLoginsModal = () => {
  const [getUnViewedBadLogins] = useGetUnViewedBadLoginsMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<UnViewedBadLogin[]>([]);

  useEffect(() => {
    if (sessionStorage.getItem(UNVIEWED_BAD_LOGINS_SHOWN_KEY) === 'true') {
      return;
    }

    let cancelled = false;

    const fetchBadLogins = async () => {
      if (!inFlightRequest) {
        inFlightRequest = (async () => {
          try {
            const response = await getUnViewedBadLogins().unwrap();
            sessionStorage.setItem(UNVIEWED_BAD_LOGINS_SHOWN_KEY, 'true');
            return response?.data ?? [];
          } catch {
            sessionStorage.setItem(UNVIEWED_BAD_LOGINS_SHOWN_KEY, 'true');
            return null;
          }
        })();
      }

      const data = await inFlightRequest;
      if (!cancelled && data && data.length > 0) {
        setItems(data);
        setIsOpen(true);
      }
    };

    fetchBadLogins();

    return () => {
      cancelled = true;
    };
  }, [getUnViewedBadLogins]);

  const handleClose = () => setIsOpen(false);

  return (
    <Dialog
      open={isOpen}
      onClose={undefined}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 520,
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          boxShadow: '0 16px 40px rgba(15, 23, 42, 0.16)',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: '#dc2626',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontWeight: 800,
              fontSize: 16,
              lineHeight: 1,
              boxShadow: '0 0 12px 4px rgba(220, 38, 38, 0.45)',
            }}
          >
            !
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
            Başarısız Giriş Denemeleri
          </Typography>
        </Box>

        <Typography sx={{ mt: 2, mb: 1.5, fontSize: '0.875rem', color: '#374151', lineHeight: 1.6 }}>
          Son başarılı girişinden bu yana hesabında başarısız giriş denemeleri gerçekleşti.
        </Typography>

        <Box
          sx={{
            maxHeight: 210,
            overflowY: 'auto',
            pr: items.length > 5 ? 0.5 : 0,
          }}
        >
          {items.map((item, index) => (
            <Box
              key={`${item.cDate}-${item.ip}-${index}`}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.25,
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                Giriş Tarihi
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
                {formatDateMinSecond(item.cDate)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
          <Button
            onClick={handleClose}
            sx={{
              minWidth: 96,
              height: 36,
              px: 3,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#ffffff',
              backgroundColor: '#dc2626',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#b91c1c',
                boxShadow: 'none',
              },
            }}
          >
            Tamam
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default UnViewedBadLoginsModal;
